import './SnoozeIssueButton.scss';

import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import dayjs from 'dayjs';
import { haltClickEventPropagation } from '@lib/events';
import { issuesActions } from '@sonar-software/sonar-issues';
import { useAction } from '@hooks';

import { ArrowLeftOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { Button, DatePicker } from 'antd';
import { getClosestCardHeader, getDirectParent } from '@lib/popups';
import { PopoverMenuButton, PopoverMenuItems } from '@ui/menu';
import React, { useCallback, useMemo, useState } from 'react';

function getRegularMenuItems(
	sonarObjectId: number,
	setShowDatepicker: (value: boolean) => void,
	handleDateSelection: (value: string) => void
): PopoverMenuItems {
	const todayMorning = dayjs().startOf('day')
		.add(9, 'hours');

	const tomorrowDate = todayMorning.clone().add(1, 'days');
	const nextWeekDate = todayMorning.clone().add(1, 'weeks');
	const oneMonthDate = todayMorning.clone().add(1, 'months');

	const menuItems: PopoverMenuItems = [
		{
			key: COPY_SPECIFIC.TOMORROW,
			label:
	<>
		<span>{ COPY_SPECIFIC.TOMORROW }</span>
		<span>{ tomorrowDate.format('dddd') }</span>
	</>,
			onClick: (e) => {
				e.domEvent.stopPropagation();

				handleDateSelection(tomorrowDate.toISOString());
			}
		},
		{
			key: COPY_SPECIFIC.NEXT_WEEK,
			label:
	<>
		<span>{ COPY_SPECIFIC.NEXT_WEEK }</span>
		<span>{ nextWeekDate.format('dddd') }</span>
	</>,
			onClick: (e) => {
				e.domEvent.stopPropagation();

				handleDateSelection(nextWeekDate.toISOString());
			}
		},
		{
			key: COPY_SPECIFIC.ONE_MONTH,
			label:
	<>
		<span>{ COPY_SPECIFIC.ONE_MONTH }</span>
		<span>{ oneMonthDate.format('dddd, MMMM Do') }</span>
	</>,
			onClick: (e) => {
				e.domEvent.stopPropagation();

				handleDateSelection(oneMonthDate.toISOString());
			}
		},
		{
			type: 'divider'
		},
		{
			key: 'select-date',
			label:
	<Button
		icon={ <ClockCircleOutlined /> }
		id={ `snooze-select-date-issue-${sonarObjectId}` }
		onClick={ (e) => {
			e.stopPropagation();

			setShowDatepicker(true);
		} }
		type='default'>
		{ COPY_SPECIFIC.SELECT_DATE }
	</Button>

		}
	];

	return menuItems;
}

function getSecondaryMenuItems(
	sonarObjectId: number,
	setShowDatepicker: (value: boolean) => void,
	handleDateSelection: (value: string) => void
): PopoverMenuItems {
	const today = dayjs().endOf('day');

	const menuItems: PopoverMenuItems = [
		{
			key: 'back',
			label:
	<Button
		className='snooze-back-button'
		icon={ <ArrowLeftOutlined /> }
		id={ `snooze-back-issue-${sonarObjectId}` }
		onClick={ (e) => {
			e.stopPropagation();

			setShowDatepicker(false);
		} }
		type='link'>
		{ COPY_SPECIFIC.BACK }
	</Button>

		},
		{
			type: 'divider'
		},
		{
			className: 'snooze-issue-calendar',
			key: 'calendar',
			label:
	<DatePicker
		defaultOpen
		disabledDate={ (currentDate) => currentDate < today }
		getPopupContainer={ getDirectParent }
		onChange={ (value) =>
			value && handleDateSelection(value.startOf('day').add(9, 'hours')
				.toISOString()) }
		onClick={ haltClickEventPropagation }
		open
		showTime={ false } />

		}
	];

	return menuItems;
}

export interface SnoozeIssueButtonsProps {
	sonarObjectId: number;
}

export const SnoozeIssueButton: React.FC<SnoozeIssueButtonsProps> = ({ sonarObjectId }) => {
	const [ showDatepicker, setShowDatepicker ] = useState(false);

	const archiveOrSnoozeIssue = useAction(issuesActions.requestPostIssuesByIdVisibility);

	const handleDateSelection = useCallback(
		(date: string) => {
			archiveOrSnoozeIssue({
				snoozeUntil: date,
				sonarObjectId
			});
		},
		[ archiveOrSnoozeIssue, sonarObjectId ]
	);

	const handleOpenChange = useCallback((value: boolean) => {
		if (!value) {
			setTimeout(() => setShowDatepicker(false), 250);
		}
	}, []);

	const menuItems = useMemo(() => {
		if (showDatepicker) {
			return getSecondaryMenuItems(sonarObjectId, setShowDatepicker, handleDateSelection);
		}

		return getRegularMenuItems(sonarObjectId, setShowDatepicker, handleDateSelection);
	}, [ sonarObjectId, showDatepicker, handleDateSelection ]);

	const overlayClassNames = classNames('snooze-issue-menu', { 'snooze-options': !showDatepicker });

	return (
		<PopoverMenuButton
			getPopupContainer={ getClosestCardHeader }
			icon={ <ClockCircleOutlined /> }
			id={ `snooze-issue-${sonarObjectId}` }
			menuItems={ menuItems }
			onClick={ haltClickEventPropagation }
			onOpenChange={ handleOpenChange }
			overlayClassName={ overlayClassNames }>
			{ COPY_SPECIFIC.SNOOZE }
		</PopoverMenuButton>
	);
};
